import React from "react";
import dynamic from "next/dynamic";
import Link from "next/link";

import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import MuiAppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";

import { PageGridPlacementType } from "../../types";

import Logo from "./Logo";
import QuickAccessButton from "./QuickAccessButton";
import {
    desktopMenuSx,
    mobileMenuButtonSx,
    mobileMenuWrapperSx,
    muiAppBarSx,
    navigationContainerSx,
    quickAccessButtonSx,
    toolbarSx,
    topLevelChevronSx,
    topLevelItemWrapperSx,
    topLevelLinkContentSx,
} from "./core/navigationStyles";
import { hasChildren, isSameMenuItem } from "./core/navigationItemUtils";
import { useNavigationMenu } from "./useNavigationMenu";
import { NavigationMenuItem } from "./core/navigationTypes";

interface NavigationProps {
    navigation: any;
    page: any;
    header?: PageGridPlacementType;
    skipLinksVisible?: boolean;
}

const AppBarMenu = dynamic(() => import("./AppBarMenu/AppBarMenu"), {
    ssr: false,
});

const MobileMenu = dynamic(() => import("./MobileMenu/MobileMenu"), {
    ssr: false,
});

/* eslint-disable react-hooks/refs -- eslint-plugin-react-hooks 7.1.1 reports false
   positives for the useNavigationMenu view model. Recheck with newer plugin versions. */
function Navigation(props: NavigationProps) {
    const { navigation, page } = props;
    const menu = useNavigationMenu(props);

    const renderDesktopMenuItem = (
        menuItem: NavigationMenuItem,
        index: number
    ) => {
        const isExpanded =
            menu.appBarMenu.isVisible &&
            isSameMenuItem(menu.appBarMenu.props.selection, menuItem);

        return (
            <Box key={menuItem.id ?? index} sx={topLevelItemWrapperSx}>
                <Link
                    data-cypress="mainNavigationLink"
                    id={`btn-${menuItem.id}`}
                    href={menuItem.selfUrl ?? "#"}
                    aria-haspopup={hasChildren(menuItem) ? "menu" : undefined}
                    aria-expanded={hasChildren(menuItem) ? isExpanded : undefined}
                    passHref
                    {...menu.desktopMenu.getLinkProps(menuItem, index)}
                >
                    <Box sx={topLevelLinkContentSx}>
                        <Typography
                            data-cypress={`mainNavigationSpan${index}`}
                            variant="navigationItem"
                            sx={menu.desktopMenu.getItemSx(menuItem, index)}
                        >
                            {menuItem.teaserTitle}
                        </Typography>

                        {hasChildren(menuItem) && (
                            <Box component="span" sx={topLevelChevronSx(isExpanded)}>
                                <ExpandMoreIcon fontSize="small" />
                            </Box>
                        )}
                    </Box>
                </Link>
            </Box>
        );
    };

    return (
        <MuiAppBar
            component="div"
            id="navigation"
            data-cypress="appBar"
            sx={muiAppBarSx(
                menu.layout.hideTopNavi,
                menu.layout.greaterThanWindowLarge
            )}
            position="sticky"
        >
            <Container maxWidth={false} sx={navigationContainerSx}>
                <Toolbar disableGutters sx={toolbarSx}>
                    <Logo navigation={navigation} />

                    {menu.desktopMenu.isVisible && (
                        <Box sx={desktopMenuSx} ref={menu.desktopMenu.appBarRef}>
                            {menu.desktopMenu.items.map(renderDesktopMenuItem)}
                        </Box>
                    )}

                    <Box sx={quickAccessButtonSx}>
                        <QuickAccessButton
                            buttonStyle={{ float: "right" }}
                            fullWidth={false}
                            page={page}
                        />
                    </Box>

                    {menu.mobileMenu.isVisible && (
                        <Box sx={mobileMenuWrapperSx}>
                            <IconButton
                                data-cypress="mobileMenuButton"
                                sx={mobileMenuButtonSx}
                                size="large"
                                aria-label="menu"
                                aria-controls="menu-appbar"
                                aria-haspopup="true"
                                onClick={menu.mobileMenu.toggle}
                                color="inherit"
                            >
                                {menu.mobileMenu.open ? (
                                    <CloseIcon data-cypress="mobileMenuCloseIcon" />
                                ) : (
                                    <MenuIcon data-cypress="mobileMenuIcon" />
                                )}
                            </IconButton>

                            <MobileMenu
                                page={page}
                                items={menu.mobileMenu.items}
                                open={menu.mobileMenu.open}
                                onClose={menu.mobileMenu.close}
                                topNaviItems={menu.mobileMenu.topNaviItems}
                                navigation={navigation}
                            />
                        </Box>
                    )}
                </Toolbar>

                {menu.appBarMenu.isVisible && (
                    <AppBarMenu {...menu.appBarMenu.props} />
                )}
            </Container>
        </MuiAppBar>
    );
}

export default Navigation;
